import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Stack,
  Chip,
  Autocomplete,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { getErrorMessage } from '@billda/shared-ui';
import {
  getNotificationScenarios,
  getNotificationEntityOptions,
  triggerTestNotification,
  type NotificationTypeDto,
  type TriggerTestNotificationRequest,
  type EntityOptionDto,
  type NotificationEntityOptionsResponse,
} from '../api/notificationsApi';

function formatTypeName(typeName: string): string {
  if (typeName === '_Raw') return 'Raw Definition';
  return typeName
    .replace('NotificationV1', '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
}

function getRequiredInputLabel(requiredInput: string): {
  label: string;
  color: 'success' | 'info' | 'warning';
} {
  if (requiredInput === 'none')
    return { label: 'Current user', color: 'success' };
  if (requiredInput === 'raw')
    return { label: 'Custom payload', color: 'warning' };
  return { label: `Requires ${requiredInput}`, color: 'info' };
}

function getOptionsForInput(
  requiredInput: string,
  entityOptions: NotificationEntityOptionsResponse | null
): EntityOptionDto[] {
  if (!entityOptions) return [];
  if (requiredInput === 'pot') return entityOptions.pots;
  if (requiredInput === 'allocation') return entityOptions.allocations;
  if (requiredInput === 'deduction') return entityOptions.deductions;
  return [];
}

export const NotificationTestingPage: React.FC = () => {
  const [types, setTypes] = useState<NotificationTypeDto[]>([]);
  const [entityOptions, setEntityOptions] =
    useState<NotificationEntityOptionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<
    Record<string, EntityOptionDto | null>
  >({});
  const [rawTypes, setRawTypes] = useState<Record<string, string>>({});
  const [rawData, setRawData] = useState<Record<string, string>>({});
  const [sendingType, setSendingType] = useState<string | null>(null);
  const [forceUniqueDedup, setForceUniqueDedup] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [typesResult, optionsResult] = await Promise.all([
        getNotificationScenarios(),
        getNotificationEntityOptions(),
      ]);

      setTypes(typesResult);
      setEntityOptions(optionsResult);
    } catch (err) {
      setError(getErrorMessage(err));
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTrigger = async (type: NotificationTypeDto) => {
    setSendingType(type.notificationType);

    const request: TriggerTestNotificationRequest = {
      notificationType: type.notificationType,
      forceUniqueDedup,
    };

    const selected = selectedEntities[type.notificationType];

    if (type.requiredInput === 'pot') request.potId = selected?.id;
    else if (type.requiredInput === 'allocation')
      request.allocationId = selected?.id;
    else if (type.requiredInput === 'deduction')
      request.deductionId = selected?.id;
    else if (type.requiredInput === 'raw') {
      request.rawNotificationType = rawTypes[type.notificationType];
      request.rawData = rawData[type.notificationType] || undefined;
    }

    try {
      const result = await triggerTestNotification(request);
      setSendingType(null);

      setSnackbar({
        open: true,
        message: result.message || 'Notification sent!',
        severity: 'success',
      });
    } catch (err) {
      setSendingType(null);

      setSnackbar({
        open: true,
        message: getErrorMessage(err),
        severity: 'error',
      });
    }
  };

  const isSendDisabled = (type: NotificationTypeDto) => {
    if (sendingType !== null) return true;
    if (type.requiredInput === 'none') return false;
    if (type.requiredInput === 'raw')
      return !rawTypes[type.notificationType]?.trim();
    return !selectedEntities[type.notificationType];
  };

  const categories = useMemo(() => {
    const grouped = new Map<string, NotificationTypeDto[]>();
    for (const type of types) {
      const existing = grouped.get(type.category);
      if (existing) {
        existing.push(type);
      } else {
        grouped.set(type.category, [type]);
      }
    }
    return grouped;
  }, [types]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        data-testid="notification-testing-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3} data-testid="notification-testing-error">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3} data-testid="notification-testing-page">
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <NotificationsIcon />
        <Typography variant="h5">Notification Testing</Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Select a notification type, pick the target entity, then trigger it. The
        notification routes through the full notification service pipeline.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={forceUniqueDedup}
            onChange={e => setForceUniqueDedup(e.target.checked)}
            data-testid="force-unique-dedup-toggle"
          />
        }
        label="Bypass Deduplication"
        sx={{ mb: 2 }}
      />

      <Stack spacing={1}>
        {[...categories.entries()].map(([category, categoryTypes]) => (
          <Accordion
            key={category}
            defaultExpanded={false}
            data-testid={`notification-category-${category}`}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight={600}>
                {category}
              </Typography>
              <Chip label={categoryTypes.length} size="small" sx={{ ml: 1 }} />
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {categoryTypes.map(type => {
                  const chipInfo = getRequiredInputLabel(type.requiredInput);
                  const options = getOptionsForInput(
                    type.requiredInput,
                    entityOptions
                  );
                  const needsEntitySelect =
                    type.requiredInput !== 'none' &&
                    type.requiredInput !== 'raw';

                  return (
                    <Card
                      key={type.notificationType}
                      variant="outlined"
                      data-testid={`notification-type-${type.notificationType}`}
                    >
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            spacing={2}
                          >
                            <Box flex={1}>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {formatTypeName(type.notificationType)}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mt: 0.5 }}
                              >
                                {type.notificationType !== '_Raw' && (
                                  <Chip
                                    label={type.notificationType}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                                <Chip
                                  label={chipInfo.label}
                                  size="small"
                                  color={chipInfo.color}
                                />
                              </Stack>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 0.5, display: 'block' }}
                              >
                                {type.description}
                              </Typography>
                            </Box>

                            {needsEntitySelect && (
                              <Autocomplete
                                size="small"
                                options={options}
                                value={
                                  selectedEntities[type.notificationType] ??
                                  null
                                }
                                onChange={(_, value) =>
                                  setSelectedEntities(prev => ({
                                    ...prev,
                                    [type.notificationType]: value,
                                  }))
                                }
                                getOptionLabel={option =>
                                  `${option.label} (${option.status})`
                                }
                                renderOption={(props, option) => (
                                  <li {...props} key={option.id}>
                                    <Stack>
                                      <Typography variant="body2">
                                        {option.label}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {option.status} —{' '}
                                        {option.id.slice(0, 8)}...
                                      </Typography>
                                    </Stack>
                                  </li>
                                )}
                                isOptionEqualToValue={(option, value) =>
                                  option.id === value.id
                                }
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    label={`Select ${type.requiredInput}`}
                                    placeholder="Search..."
                                  />
                                )}
                                sx={{ minWidth: 340 }}
                                data-testid={`entity-select-${type.notificationType}`}
                              />
                            )}

                            <Button
                              variant="contained"
                              startIcon={
                                sendingType === type.notificationType ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <SendIcon />
                                )
                              }
                              disabled={isSendDisabled(type)}
                              onClick={() => handleTrigger(type)}
                              data-testid={`trigger-btn-${type.notificationType}`}
                            >
                              Send
                            </Button>
                          </Stack>

                          {type.requiredInput === 'raw' && (
                            <Stack spacing={1.5}>
                              <TextField
                                size="small"
                                label="Notification Type"
                                placeholder="e.g. PotApprovedNotificationV1"
                                value={rawTypes[type.notificationType] || ''}
                                onChange={e =>
                                  setRawTypes(prev => ({
                                    ...prev,
                                    [type.notificationType]: e.target.value,
                                  }))
                                }
                                fullWidth
                                data-testid="raw-notification-type-input"
                              />
                              <TextField
                                size="small"
                                label="JSON Data (optional)"
                                placeholder='{"key": "value"}'
                                value={rawData[type.notificationType] || ''}
                                onChange={e =>
                                  setRawData(prev => ({
                                    ...prev,
                                    [type.notificationType]: e.target.value,
                                  }))
                                }
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={6}
                                data-testid="raw-notification-data-input"
                              />
                            </Stack>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        data-testid="notification-snackbar"
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
